import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider',
    );
  }
  return context;
};

// Subscription limits (must match server-side enforcement)
const LIMITS = {
  free: {
    aiImprovements: 1,        // 1 abandoned-patent improvement / month
    savedPatents: 2,          // 2 saved abandoned patents (total)
    savedIdeas: 2,            // 2 saved ideas (total)
    patentFilings: 0,         // No free patent filings
    patentFilingPrice: null,  // No individual patent purchases allowed
    // Unlimited searches for all users
    abandonedSearches: Infinity,
    discoveryDbPatents: Infinity,
    discoverySerpPatents: Infinity,
    discoveryTotal: Infinity,
  },
  starter: {
    aiImprovements: 8,        // grandfathered legacy tier — full Pro-level access
    savedPatents: Infinity,
    savedIdeas: Infinity,
    patentFilings: 1,         // 1 free patent filing per month
    patentFilingPrice: null,  // No individual patent purchases - must upgrade to Pro
    // Unlimited searches for all users
    abandonedSearches: Infinity,
    discoveryDbPatents: Infinity,
    discoverySerpPatents: Infinity,
    discoveryTotal: Infinity,
  },
  pro: {
    aiImprovements: 8,        // AI improvements/month
    savedPatents: Infinity,
    savedIdeas: Infinity,
    patentFilings: 4,         // 4 free patent filings per month
    patentFilingPrice: 5.00,  // $5 per additional patent beyond monthly limit
    // Unlimited searches for all users
    abandonedSearches: Infinity,
    discoveryDbPatents: Infinity,
    discoverySerpPatents: Infinity,
    discoveryTotal: Infinity,
  },
  // 'business' is the new top tier — display name "Premium" — everything in Pro plus
  // competitive monitoring. Plan key is deliberately 'business', NOT 'premium' (that string
  // is a retired value that some limit checks fall back to Free on). Pro-equivalent limits.
  // See docs/competitor-monitoring-plan.md §9.
  business: {
    aiImprovements: 8,
    savedPatents: Infinity,
    savedIdeas: Infinity,
    patentFilings: 4,
    patentFilingPrice: 5.00,
    abandonedSearches: Infinity,
    discoveryDbPatents: Infinity,
    discoverySerpPatents: Infinity,
    discoveryTotal: Infinity,
  },
};

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if usage should be reset (monthly reset)
  const shouldResetUsage = (lastResetDate) => {
    if (!lastResetDate) return true;

    const lastReset = new Date(lastResetDate);
    const now = new Date();

    // Check if we're in a new month
    return (
      lastReset.getMonth() !== now.getMonth() ||
      lastReset.getFullYear() !== now.getFullYear()
    );
  };

  // Fetch subscription data from Firebase
  const fetchSubscriptionData = async () => {
    if (!user) {
      setSubscriptionData(null);
      setLoading(false);
      return;
    }

    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const data = userDoc.data();

          // Subscription data should already exist from user creation
          // If it doesn't exist, something is wrong - log warning and use safe defaults
          if (!data.subscription) {
            console.warn(
              '⚠️ User document exists but missing subscription data. User ID:',
              user.uid,
            );
            console.warn(
              '⚠️ This should not happen - subscription should be created during signup.',
            );
            // Use default free plan locally but don't write to database
            // This prevents auto-upgrading or accidentally resetting premium users
            setSubscriptionData({
              plan: 'free',
              status: 'active',
              usage: {
                abandonedSearches: 0,
                aiImprovements: 0,
                ideas: 0,
                patentFilings: 0,
              },
              lastResetDate: new Date().toISOString(),
            });
            return;
          }

          let subscription = data.subscription;

          // Check if usage should be reset (monthly reset)
          if (shouldResetUsage(subscription.lastResetDate)) {
            subscription = {
              ...subscription,
              usage: {
                abandonedSearches: 0,
                aiImprovements: 0,
                ideas: 0,
                patentFilings: 0,
              },
              lastResetDate: new Date().toISOString(),
            };

            // Update in Firebase
            await updateDoc(userDocRef, {
              'subscription.usage': subscription.usage,
              'subscription.lastResetDate': subscription.lastResetDate,
            });
          }

          setSubscriptionData(subscription);
          return subscription;
        } else {
          // User document doesn't exist - this shouldn't happen
          console.error(
            '❌ User authenticated but no Firestore document found. User ID:',
            user.uid,
          );
          setSubscriptionData(null);
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
      } finally {
        setLoading(false);
      }
  };

  // Refresh subscription data manually
  const refreshSubscription = async () => {
    return await fetchSubscriptionData();
  };

  useEffect(() => {
    fetchSubscriptionData();
  }, [user]);

  // Get current plan limits
  const getCurrentLimits = () => {
    const plan = subscriptionData?.plan || 'free';
    return LIMITS[plan];
  };

  // Check if user has reached a specific limit
  const hasReachedLimit = (feature) => {
    if (!subscriptionData) return false;
    const limits = getCurrentLimits();
    const usage = subscriptionData.usage || {};

    switch (feature) {
      case 'abandonedSearches':
        return usage.abandonedSearches >= limits.abandonedSearches;
      case 'aiImprovements':
        return usage.aiImprovements >= limits.aiImprovements;
      case 'patentFilings':
        return usage.patentFilings >= limits.patentFilings;
      case 'ideas':
        return false; // Ideas have no limit
      default:
        return false;
    }
  };

  // Increment usage counter
  const incrementUsage = async (feature) => {
    if (!user || !subscriptionData) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      const newUsage = {
        ...subscriptionData.usage,
        [feature]: (subscriptionData.usage[feature] || 0) + 1,
      };

      await updateDoc(userDocRef, {
        'subscription.usage': newUsage,
      });

      setSubscriptionData((prev) => ({
        ...prev,
        usage: newUsage,
      }));
    } catch (error) {
      console.error('Error incrementing usage:', error);
    }
  };

  // Get remaining usage for a feature
  const getRemainingUsage = (feature) => {
    if (!subscriptionData) return 0;
    const limits = getCurrentLimits();
    const usage = subscriptionData.usage || {};
    const current = usage[feature] || 0;
    return Math.max(0, limits[feature] - current);
  };

  /*
   * Subscription states that still grant access.
   *
   * `trialing` is NOT optional here. Stripe sets it for the whole free period and every new
   * subscription now begins there, so requiring 'active' would lock a customer out of the exact
   * three days they just handed over a card for. The Starter -> Pro upgrade already had to work
   * around this by writing 'active' over Stripe's own status; that workaround exists because
   * this list did not.
   *
   * `past_due` too: a failed renewal is usually an expired card and Stripe keeps retrying for
   * days. Access ends at 'canceled' and 'unpaid', not at the first failed charge.
   *
   * Mirrors LIVE_STATUSES in src/services/entitlement.service.ts, which is the server's copy of
   * the same rule.
   */
  const LIVE_STATUSES = ['active', 'trialing', 'past_due'];
  const isLive = () => LIVE_STATUSES.includes(subscriptionData?.status);

  // Check if user is premium (kept for backwards compatibility, but checks for any paid tier).
  // NOTE: this drives UI like the Manage-billing button on Profile, so 'business' MUST be
  // included here or a paying $99/mo user loses billing management. See plan doc §9.
  const isPremium = () => {
    return (
      (subscriptionData?.plan === 'starter' ||
        subscriptionData?.plan === 'pro' ||
        subscriptionData?.plan === 'business') &&
      isLive()
    );
  };

  // Whether the user is on the monitoring tier. Monitoring is 'business'-exclusive: Free and
  // Pro get zero access (no trial monitor, no teaser feed). See plan doc §9.
  const isBusiness = () => {
    return subscriptionData?.plan === 'business' && isLive();
  };

  // Check if user is on starter plan
  const isStarter = () => {
    return subscriptionData?.plan === 'starter' && isLive();
  };

  // Check if user is on pro plan
  const isPro = () => {
    return subscriptionData?.plan === 'pro' && isLive();
  };

  // Update subscription plan (called after successful payment)
  const updateSubscriptionPlan = async (plan) => {
    if (!user) return;

    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        'subscription.plan': plan,
        'subscription.status': 'active',
        'subscription.updatedAt': new Date().toISOString(),
      });

      setSubscriptionData((prev) => ({
        ...prev,
        plan,
        status: 'active',
        updatedAt: new Date().toISOString(),
      }));
    } catch (error) {
      console.error('Error updating subscription plan:', error);
    }
  };

  const value = {
    subscriptionData,
    loading,
    isPremium: isPremium(),
    isStarter: isStarter(),
    isPro: isPro(),
    isBusiness: isBusiness(),
    hasMonitoring: isBusiness(),
    currentPlan: subscriptionData?.plan || 'free',
    // Whether the account may use the product at all — the question the paywall asks.
    hasAccess: isPremium(),
    isTrialing: subscriptionData?.status === 'trialing',
    limits: getCurrentLimits(),
    hasReachedLimit,
    incrementUsage,
    getRemainingUsage,
    updateSubscriptionPlan,
    refreshSubscription,
    setSubscriptionData,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
